import * as components from '@/constants/nuxt-ui/configs'

export default defineAppConfig(
	{
		ui: {
			colors: {
				primary: 'orange',
				secondary: 'blue',
				neutral: 'gray',
				success: 'green',
				info: 'blue',
				warning: 'yellow',
				error: 'red'
			},
			container: {
				base: 'w-full max-w-(--ui-container) mx-auto px-4 sm:px-6 lg:px-8 xl:px-0'
			},
			button: components.NUXT_UI_BUTTON,
			input: components.NUXT_UI_INPUT,
			select: components.NUXT_UI_SELECT,
			toast: components.NUXT_UI_TOAST,
			carousel: components.NUXT_UI_CAROUSEL,
			checkbox: components.NUXT_UI_CHECKBOX,
			modal: components.NUXT_UI_MODAL,
			navigationMenu: components.NUXT_UI_NAVIGATION_MENU,
			tabs: components.NUXT_UI_TABS,
			formField: components.NUXT_UI_FORM_FIELD
		},
		uiPro: {
			banner: components.NUXT_UI_PRO_BANNER,
			blogPost: components.NUXT_UI_PRO_BLOG_POST,
			blogPosts: components.NUXT_UI_PRO_BLOG_POSTS,
			header: components.NUXT_UI_PRO_HEADER,
			footer: components.NUXT_UI_PRO_FOOTER,
			footerColumns: components.NUXT_UI_PRO_FOOTER_COLUMNS,
			page: {
				slots: {
					root: 'flex flex-col lg:grid lg:grid-cols-10 lg:gap-10',
					left: 'lg:col-span-2',
					center: 'lg:col-span-8',
					right: 'lg:col-span-2 order-first lg:order-last'
				},
				variants: {
					left: {
						true: ''
					},
					right: {
						true: ''
					}
				},
				compoundVariants: [
					{
						left: true,
						right: true,
						class: {
							center: 'lg:col-span-6'
						}
					},
					{
						left: false,
						right: false,
						class: {
							center: 'lg:col-span-10 h-full'
						}
					}
				]
			},
			pageBody: {
				base: 'mt-8 pb-24 space-y-12 relative isolate overflow-x-hidden'
			},
			pageHero: {
				slots: {
					root: 'relative isolate',
					container: 'flex flex-col items-center lg:py-40 lg:grid py-24 sm:py-32 gap-16 sm:gap-x-0 sm:gap-y-24 relative isolate',
					wrapper: '-mt-16 lg:-mt-36',
					headline: 'mb-4',
					title: 'text-4xl sm:text-5xl text-pretty tracking-tight font-semibold text-highlighted',
					description: 'text-lg sm:text-xl/8 text-muted',
					links: 'flex flex-col sm:flex-row gap-2 w-full flex-grow'
				},
				variants: {
					orientation: {
						horizontal: {
							container: 'lg:grid-cols-2 lg:items-center',
							description: 'text-pretty' // https://tailwindcss.com/docs/text-wrap#pretty-text-wrapping
						},
						vertical: {
							container: '',
							headline: 'justify-center',
							wrapper: 'text-center',
							description: 'text-balance', // https://tailwindcss.com/docs/text-wrap#balanced-text-wrapping
							links: 'justify-center'
						}
					},
					reverse: {
						true: {
							wrapper: 'order-last sm:order-last lg:order-first'
						}
					},
					headline: {
						true: {
							headline: 'font-semibold text-primary flex items-center gap-1.5'
						}
					},
					title: {
						true: {
							description: 'mt-6'
						}
					}
				}
			},
			pageSection: {
				slots: {
					root: 'relative isolate',
					container: 'flex flex-col lg:grid py-0 sm:py-0 lg:py-20 gap-8 sm:gap-12',
					wrapper: '',
					headline: 'mb-3',
					leading: 'flex items-center mb-6',
					leadingIcon: 'size-16 shrink-0 text-primary',
					title: 'text-2xl sm:text-3xl lg:text-4xl text-pretty tracking-tight font-semibold text-highlighted',
					description: 'text-base sm:text-lg text-muted',
					links: 'mt-8 flex flex-wrap gap-x-6 gap-y-3',
					features: 'mt-8 grid'
				},
				variants: {
					orientation: {
						horizontal: {
							container: 'lg:flex-row lg:items-center lg:grid-cols-auto lg:flex',
							description: 'text-pretty',
							features: 'gap-4'
						},
						vertical: {
							container: '',
							headline: 'justify-center',
							leading: 'justify-center',
							title: 'text-center',
							description: 'text-center text-balance',
							links: 'justify-center',
							features: 'sm:grid-cols-2 lg:grid-cols-3 gap-8'
						}
					},
					reverse: {
						true: {
							wrapper: 'order-last sm:order-last lg:order-last'
						}
					},
					headline: {
						true: {
							headline: 'font-semibold text-primary flex items-center gap-1.5'
						}
					},
					title: {
						true: {
							description: 'mt-6'
						}
					},
					description: {
						true: ''
					},
					features: {
						true: ''
					}
				},
				compoundVariants: [
					{
						orientation: 'vertical',
						title: true,
						class: {
							features: 'mt-16'
						}
					},
					{
						orientation: 'vertical',
						description: true,
						class: {
							features: 'mt-16'
						}
					},
					{
						orientation: 'vertical',
						features: true,
						class: {
							links: 'mt-16',
							container: 'lg:flex-row lg:items-start lg:grid-cols-auto lg:flex'
						}
					},
					{
						orientation: 'horizontal',
						features: true,
						class: {
							links: 'mt-16',
							container: 'lg:flex-row lg:items-start lg:grid-cols-auto lg:flex'
						}
					}
				]
			},
			pageFeature: {
				slots: {
					root: 'relative',
					wrapper: '',
					leading: 'inline-flex items-center justify-center',
					leadingIcon: 'size-6 shrink-0 text-primary',
					title: 'text-base text-pretty font-semibold text-highlighted text-xl',
					description: 'text-lg text-pretty text-muted'
				},
				variants: {
					orientation: {
						horizontal: {
							root: 'flex items-start gap-2.5',
							leading: 'p-0.5'
						},
						vertical: {
							leading: 'mb-2.5'
						}
					},
					title: {
						true: {
							description: 'mt-1'
						}
					}
				}
			},
			pageColumns: {
				base: 'relative column-1 md:columns-2 lg:columns-3 gap-8 space-y-8 *:break-inside-avoid-column *:will-change-transform'
			},
			pageCard: {
				slots: {
					root: 'relative flex rounded-xl shadow-lg border-b-8 border-t-inverted border-x-inverted border-b-inverted transition-all duration-200 ease-in-out',
					spotlight: 'absolute inset-0 rounded-[inherit] pointer-events-none bg-default/90',
					container: 'relative flex flex-col flex-1 lg:grid gap-x-8 gap-y-4 p-4 sm:p-6',
					wrapper: 'flex flex-col flex-1',
					header: 'mb-4',
					body: 'flex-1',
					footer: 'pt-4 mt-auto',
					leading: 'inline-flex items-center mb-2.5',
					leadingIcon: 'size-5 shrink-0 text-primary',
					title: 'text-base text-pretty font-semibold text-highlighted',
					description: 'text-[15px] text-pretty'
				},
				variants: {
					orientation: {
						horizontal: {
							container: 'lg:grid-cols-2 lg:items-center'
						},
						vertical: {
							container: ''
						}
					},
					reverse: {
						true: {
							wrapper: 'lg:order-last'
						}
					},
					variant: {
						solid: {
							root: 'bg-default text-default rounded-xl shadow-lg',
							title: 'text-default',
							description: 'text-muted'
						},
						outline: {
							root: 'bg-default ring ring-dimmed',
							description: 'text-muted'
						},
						soft: {
							root: 'bg-elevated/50',
							description: 'text-toned'
						},
						subtle: {
							root: 'bg-elevated/50 ring ring-default',
							description: 'text-toned'
						},
						ghost: {
							description: 'text-muted'
						},
						naked: {
							container: 'p-0 sm:p-0',
							description: 'text-muted'
						}
					},
					to: {
						true: {
							root: 'transition'
						}
					},
					title: {
						true: {
							description: 'mt-1'
						}
					},
					highlight: {
						true: {
							root: 'border-b-8 border-t-inverted border-x-inverted border-b-primary ring-0'
						}
					},
					highlightColor: {
						primary: '--ui-primary',
						secondary: '--ui-secondary',
						success: '',
						info: '',
						warning: '',
						error: '',
						neutral: ''
					},
					spotlight: {
						true: {
							root: '[--spotlight-size:400px] before:absolute before:-inset-px before:pointer-events-none before:rounded-[inherit] before:bg-[radial-gradient(var(--spotlight-size)_var(--spotlight-size)_at_calc(var(--spotlight-x,0px))_calc(var(--spotlight-y,0px)),var(--spotlight-color),transparent_70%)]'
						}
					},
					spotlightColor: {
						primary: '--ui-primary',
						secondary: '',
						success: '',
						info: '',
						warning: '',
						error: '',
						neutral: ''
					}
				},
				compoundVariants: [
					{
						variant: 'solid',
						to: true,
						class: {
							root: 'hover:bg-muted/90'
						}
					},
					{
						variant: 'outline',
						to: true,
						class: {
							root: 'hover:bg-elevated/50'
						}
					},
					{
						variant: 'soft',
						to: true,
						class: {
							root: 'hover:bg-elevated'
						}
					},
					{
						variant: 'subtle',
						to: true,
						class: {
							root: 'hover:bg-elevated'
						}
					},
					{
						variant: 'subtle',
						to: true,
						highlight: false,
						class: {
							root: 'hover:ring-accented'
						}
					},
					{
						variant: 'ghost',
						to: true,
						class: {
							root: 'hover:bg-elevated/50'
						}
					},
					{
						highlightColor: 'primary',
						highlight: true,
						class: {
							root: 'ring-primary'
						}
					},
					{
						highlightColor: 'neutral',
						highlight: true,
						class: {
							root: 'ring-inverted'
						}
					},
					{
						spotlightColor: 'primary',
						spotlight: true,
						class: {
							root: '[--spotlight-color:var(--ui-primary)]'
						}
					},
					{
						spotlightColor: 'secondary',
						spotlight: true,
						class: {
							root: '[--spotlight-color:var(--ui-secondary)]'
						}
					},
					{
						spotlightColor: 'success',
						spotlight: true,
						class: {
							root: '[--spotlight-color:var(--ui-success)]'
						}
					},
					{
						spotlightColor: 'info',
						spotlight: true,
						class: {
							root: '[--spotlight-color:var(--ui-info)]'
						}
					},
					{
						spotlightColor: 'warning',
						spotlight: true,
						class: {
							root: '[--spotlight-color:var(--ui-warning)]'
						}
					},
					{
						spotlightColor: 'error',
						spotlight: true,
						class: {
							root: '[--spotlight-color:var(--ui-error)]'
						}
					},
					{
						spotlightColor: 'neutral',
						spotlight: true,
						class: {
							root: '[--spotlight-color:var(--ui-bg-inverted))]'
						}
					}
				],
				defaultVariants: {
					variant: 'outline',
					highlightColor: 'primary',
					spotlightColor: 'primary'
				}
			},
			pageMarquee: {
				slots: {
					root: 'group relative flex items-center overflow-hidden gap-(--gap) [--gap:--spacing(16)] [--duration:20s]',
					content: 'flex items-center shrink-0 justify-around gap-(--gap) min-w-max'
				},
				variants: {
					orientation: {
						horizontal: {
							content: 'w-full'
						},
						vertical: {
							content: 'h-full'
						}
					},
					pauseOnHover: {
						true: {
							content: 'group-hover:[animation-play-state:paused]'
						}
					},
					reverse: {
						true: {
							content: '[animation-direction:reverse]'
						}
					},
					overlay: {
						true: {
							root: 'before:absolute before:pointer-events-none before:content-[""] before:z-2 before:from-default before:to-transparent after:absolute after:pointer-events-none after:content-[""] after:z-2 after:from-default after:to-transparent'
						}
					}
				},
				compoundVariants: [
					{
						orientation: 'horizontal',
						class: {
							root: 'flex-row',
							content: 'flex-row animate-[marquee_var(--duration)_linear_infinite] rtl:animate-[marquee-rtl_var(--duration)_linear_infinite] backface-hidden'
						}
					},
					{
						orientation: 'horizontal',
						overlay: true,
						class: {
							root: 'before:inset-y-0 before:left-0 before:h-full before:w-1/3 before:bg-gradient-to-r after:inset-y-0 after:right-0 after:h-full after:w-1/3 after:bg-gradient-to-l backface-hidden'
						}
					},
					{
						orientation: 'vertical',
						class: {
							root: 'flex-col',
							content: 'flex-col animate-[marquee-vertical_var(--duration)_linear_infinite] rtl:animate-[marquee-vertical-rtl_var(--duration)_linear_infinite] h-[fit-content] backface-hidden'
						}
					},
					{
						orientation: 'vertical',
						overlay: true,
						class: {
							root: 'before:inset-x-0 before:top-0 before:w-full before:h-1/3 before:bg-gradient-to-b after:inset-x-0 after:bottom-0 after:w-full after:h-1/3 after:bg-gradient-to-t backface-hidden'
						}
					}
				]
			},
			user: {
				slots: {
					root: 'relative group/user',
					wrapper: '',
					name: 'font-medium',
					description: 'text-muted',
					avatar: 'shrink-0'
				},
				variants: {
					orientation: {
						horizontal: {
							root: 'flex items-center'
						},
						vertical: {
							root: 'flex flex-col'
						}
					},
					to: {
						true: {
							name: 'text-default peer-hover:text-highlighted transition-colors',
							description: 'peer-hover:text-toned transition-colors',
							avatar: 'transform transition-transform duration-200 group-hover/user:scale-115'
						},
						false: {
							name: 'text-highlighted',
							description: ''
						}
					},
					size: {
						'3xs': {
							root: 'gap-1',
							wrapper: 'flex items-center gap-1',
							name: 'text-xs',
							description: 'text-xs'
						},
						'2xs': {
							root: 'gap-1.5',
							wrapper: 'flex items-center gap-1.5',
							name: 'text-xs',
							description: 'text-xs'
						},
						'xs': {
							root: 'gap-1.5',
							wrapper: 'flex items-center gap-1.5',
							name: 'text-xs',
							description: 'text-xs'
						},
						'sm': {
							root: 'gap-2',
							name: 'text-xs',
							description: 'text-xs'
						},
						'md': {
							root: 'gap-2',
							name: 'text-sm',
							description: 'text-xs'
						},
						'lg': {
							root: 'gap-2.5',
							name: 'text-sm',
							description: 'text-sm'
						},
						'xl': {
							root: 'gap-2.5',
							name: 'text-base',
							description: 'text-sm'
						},
						'2xl': {
							root: 'gap-3',
							name: 'flex text-base',
							description: 'text-base'
						},
						'3xl': {
							root: 'gap-3',
							name: 'text-lg',
							description: 'text-base'
						}
					}
				},
				defaultVariants: {
					size: 'md'
				}
			}
		}
	}
)
